import React, { useContext, useState } from 'react';
import { useMemo } from 'react';
import { useEffect } from 'react';

import Axios from 'axios';

import styled from 'styled-components';

import { ListContext } from '@contexts/ListContext';
import { useProjectContext, useProjectContextDispatch, reloadProjectAccountingData } from '@contexts/ProjectContext';
import { useUserPermission } from '@contexts/UserContext';

import { forceReloadProjectStatements, useProjectStatements } from '@hooks/API/Project/UseProjectStatements';
import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';

import EmptyState from '@components/Gui/EmptyState';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import InvoiceDrawer from '@components/InvoicesAndCreditNotes/InvoiceDrawer';

import InvoiceItem from './InvoiceItem';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div``;

const ProjectInvoicesCard = () => {
    const { projectId } = useProjectContext();
    const projectDispatch = useProjectContextDispatch();
    const { data: statements, isFetching: loading } = useProjectStatements(projectId, 'Client');
    const [invoices, setInvoices] = useState([]);
    const { cachedList } = useContext(ListContext);
    const paymentTerms = cachedList.paymentTerms.items;
    const bankAccounts = cachedList.bankAccounts.items;
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const accessAccounting = useUserPermission('AccessAccounting');

    const handleInvoiceClick = (invoiceID, invoiceTypeName) => {
        if (accessAccounting) {
            setSelectedInvoice(invoiceID);
        }
    };

    const handleCloseInvoiceDrawer = () => {
        forceReloadProjectStatements(projectId, 'Client');
        reloadProjectAccountingData(projectId, projectDispatch);
        forceReloadProjectTransactions(projectId);
        setSelectedInvoice(null);
    };

    const invoiceIDs = useMemo(() => {
        let invoiceIDs = [];
        if (statements && statements.length) {
            for (const contacts of statements) {
                for (const billinfo of contacts.BillingInfos) {
                    for (const transaction of billinfo.Transactions) {
                        for (const statement of transaction.Statements) {
                            if (statement.InvoiceID && !invoiceIDs.includes(statement.InvoiceID)) {
                                invoiceIDs.push(statement.InvoiceID);
                            }
                        }
                    }
                }
            }
        }
        return invoiceIDs;
    }, [statements]);

    useEffect(() => {
        const getInvoice = async (invoiceID) => {
            try {
                const response = await Axios.get(`invoices/${invoiceID}`);
                return response;
            } catch (error) {
                console.error(error);
                return null;
            }
        };

        if (invoiceIDs && invoiceIDs.length) {
            setInvoices([]);
            for (const invoiceID of invoiceIDs) {
                getInvoice(invoiceID).then((response) => {
                    if (response) {
                        setInvoices((invoices) => [...invoices, response]);
                    }
                });
            }
        }
    }, [invoiceIDs]);

    return (
        <NwCard title="Invoices" icon={byPrefixAndName.fasl['file-invoice']}>
            {/* <BuildingFeature /> */}
            {invoices && invoices.length > 0 ? (
                <Wrapper>
                    {invoices.map((invoice) => (
                        <InvoiceItem
                            key={invoice.ID}
                            invoice={invoice}
                            paymentTerms={paymentTerms}
                            bankAccounts={bankAccounts}
                            onInvoiceClick={handleInvoiceClick}
                            hasAccountingAccess={accessAccounting}
                        />
                    ))}
                    {selectedInvoice && (
                        <InvoiceDrawer invoiceId={selectedInvoice} onClose={handleCloseInvoiceDrawer} />
                    )}
                </Wrapper>
            ) : (
                <Wrapper>
                    <EmptyState message="No invoices" />
                </Wrapper>
            )}
        </NwCard>
    );
};

export default ProjectInvoicesCard;
